import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, apiErrorText, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Eye } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY = {
  name: "", document: "", email: "", phone: "", company: "",
  address_street: "", address_number: "", address_complement: "",
  address_neighborhood: "", address_city: "", address_state: "",
  address_zip: "", notes: "",
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/clients", { params: q ? { q } : {} });
      setClients(data);
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Cliente atualizado" : "Cliente criado");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/clients/${deleteId}`);
      toast.success("Cliente removido");
      setDeleteId(null); load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  return (
    <Layout
      title="Clientes"
      subtitle="Cadastro de contratantes de serviços de inspeção aérea"
      actions={
        <Button onClick={openNew} data-testid="new-client-btn" className="gap-2">
          <Plus size={16} weight="bold" /> Novo cliente
        </Button>
      }
    >
      <div className="mb-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF/CNPJ ou e-mail..."
            className="pl-9" data-testid="client-search" />
        </div>
      </div>

      <div className="tech-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="label-tech text-muted-foreground p-4">Nome</th>
              <th className="label-tech text-muted-foreground p-4">Documento</th>
              <th className="label-tech text-muted-foreground p-4">Contato</th>
              <th className="label-tech text-muted-foreground p-4">Cidade/UF</th>
              <th className="label-tech text-muted-foreground p-4">Cadastro</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="clients-list">
            {clients.length === 0 ? (
              <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">Nenhum cliente cadastrado</td></tr>
            ) : clients.map((c) => (
              <tr key={c.id} className="border-b border-border hover:bg-secondary/30" data-testid={`client-row-${c.id}`}>
                <td className="p-4 cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                  <div className="font-bold hover:text-accent transition-colors">{c.name}</div>
                  {c.company && <div className="text-xs text-muted-foreground">{c.company}</div>}
                </td>
                <td className="p-4 mono text-sm">{c.document || "—"}</td>
                <td className="p-4 text-sm">
                  <div>{c.email || "—"}</div>
                  <div className="text-muted-foreground">{c.phone || ""}</div>
                </td>
                <td className="p-4 text-sm">{c.address_city ? `${c.address_city}/${c.address_state || ""}` : "—"}</td>
                <td className="p-4 text-sm text-muted-foreground">{formatDate(c.created_at)}</td>
                <td className="p-4">
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`view-client-${c.id}`}>
                      <Eye size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)} data-testid={`delete-client-${c.id}`}>
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Nome / Razão social *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" required />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">CPF / CNPJ</Label>
              <Input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} data-testid="client-document" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Empresa</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Endereço da inspeção</Label>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input className="md:col-span-3" placeholder="Rua / Avenida" value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} />
                <Input placeholder="Número" value={form.address_number} onChange={(e) => setForm({ ...form, address_number: e.target.value })} />
                <Input placeholder="Complemento" value={form.address_complement} onChange={(e) => setForm({ ...form, address_complement: e.target.value })} />
                <Input placeholder="Bairro" value={form.address_neighborhood} onChange={(e) => setForm({ ...form, address_neighborhood: e.target.value })} />
                <Input placeholder="Cidade" value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} />
                <Input placeholder="UF" maxLength={2} value={form.address_state} onChange={(e) => setForm({ ...form, address_state: e.target.value.toUpperCase() })} />
              </div>
              <Input placeholder="CEP" value={form.address_zip} onChange={(e) => setForm({ ...form, address_zip: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Observações</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-client-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover cliente?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-client">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
